'use client'

import type { ReactNode } from 'react'
import {
  IconBookmark,
  IconCopy,
  IconDots,
  IconPlus,
  IconVolume
} from '@tabler/icons-react'
import { toast } from 'sonner'
import { LoginDialog } from '@/components/auth/login-dialog'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'
import { useAuth } from '@/hooks/useAuth'
import { saveSentenceToDeck } from '@/lib/study'
import { cn } from '@/lib/utils'
import { saveReaderSentence } from '@/lib/reader/saved-sentences'
import type { ReaderBlock, ReaderContent } from '@/types/reader'
import type { SentenceMineInput, StudyDeckWithCounts } from '@/types/study'
import { useReaderSentenceSave } from './reader-save-sentence-button'

type ReaderSentenceActionsProps = {
  block: ReaderBlock
  content: ReaderContent
  decks: StudyDeckWithCounts[]
  lyricsMode: boolean
  sentenceText: string
  onPlay: () => void
}

export function ReaderSentenceActions({
  block,
  content,
  decks,
  lyricsMode,
  sentenceText,
  onPlay
}: ReaderSentenceActionsProps) {
  const { user } = useAuth()
  const {
    saved: defaultSentenceSaved,
    isPending: savingDefaultSentence,
    saveSentence: saveToSentenceMining
  } = useReaderSentenceSave({
    block,
    content,
    sentenceText
  })
  const otherSentenceDecks = decks.filter((deck) => !deck.default_kind)
  const sentenceInput: SentenceMineInput = {
    text: sentenceText,
    translation: block.translation ?? null,
    sourceTitle: content.title,
    sourceType: content.sourceType,
    contentId: content.id,
    blockId: block.id,
    sentenceId: block.sourceSentenceId ?? block.id,
    grammarConceptIds: Array.from(
      new Set((block.grammarUsages ?? []).map((usage) => usage.conceptId))
    ),
    readerHref:
      typeof window !== 'undefined'
        ? `${window.location.pathname}${window.location.search}`
        : null
  }

  const copyText = async (text: string, message: string) => {
    if (!text || typeof navigator === 'undefined') return
    await navigator.clipboard?.writeText(text)
    toast.success(message)
  }

  const saveToDeck = async (deckId: string, deckName: string) => {
    if (!user?.id) return
    await saveSentenceToDeck(deckId, sentenceInput)
    saveReaderSentence(content, block, sentenceText)
    toast.success(`Saved to ${deckName}`)
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label="Sentence actions"
          className={cn(
            'bg-background/88 text-muted-foreground hover:text-foreground absolute top-1 right-1 z-10 h-8 w-8 rounded-full opacity-100 shadow-xs backdrop-blur transition md:opacity-0 md:group-focus-within:opacity-100 md:group-hover:opacity-100',
            lyricsMode && 'right-2'
          )}
          onClick={(event) => event.stopPropagation()}
        >
          <IconDots className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-56 p-1"
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <SentenceActionItem
          icon={<IconCopy className="h-4 w-4" />}
          onClick={() => copyText(sentenceText, 'Sentence copied')}
        >
          Copy sentence
        </SentenceActionItem>
        {block.translation && (
          <SentenceActionItem
            icon={<IconCopy className="h-4 w-4" />}
            onClick={() =>
              copyText(block.translation || '', 'Translation copied')
            }
          >
            Copy translation
          </SentenceActionItem>
        )}
        {user?.id ? (
          <SentenceActionItem
            icon={<IconBookmark className="h-4 w-4" />}
            disabled={savingDefaultSentence || defaultSentenceSaved}
            onClick={() => {
              void saveToSentenceMining()
            }}
          >
            {savingDefaultSentence
              ? 'Saving...'
              : defaultSentenceSaved
                ? 'Saved'
                : 'Save sentence'}
          </SentenceActionItem>
        ) : (
          <LoginDialog>
            <SentenceActionButton icon={<IconBookmark className="h-4 w-4" />}>
              Save sentence
            </SentenceActionButton>
          </LoginDialog>
        )}
        {user?.id &&
          otherSentenceDecks.map((deck) => (
            <SentenceActionItem
              key={deck.id}
              icon={<IconPlus className="h-4 w-4" />}
              onClick={() => saveToDeck(deck.id, deck.name)}
            >
              Save to {deck.name}
            </SentenceActionItem>
          ))}
        <SentenceActionItem
          icon={<IconVolume className="h-4 w-4" />}
          onClick={onPlay}
        >
          {lyricsMode ? 'Go to line' : 'Play sentence'}
        </SentenceActionItem>
      </PopoverContent>
    </Popover>
  )
}

function SentenceActionItem({
  icon,
  children,
  disabled,
  onClick
}: {
  icon: ReactNode
  children: ReactNode
  disabled?: boolean
  onClick: () => void | Promise<void>
}) {
  return (
    <SentenceActionButton icon={icon} disabled={disabled} onClick={onClick}>
      {children}
    </SentenceActionButton>
  )
}

function SentenceActionButton({
  icon,
  children,
  disabled,
  onClick
}: {
  icon: ReactNode
  children: ReactNode
  disabled?: boolean
  onClick?: () => void | Promise<void>
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      className="hover:bg-accent focus-visible:ring-ring/40 flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition focus-visible:ring-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50"
      onClick={(event) => {
        event.stopPropagation()
        if (!onClick) return
        void Promise.resolve(onClick()).catch((error) => {
          toast.error(error instanceof Error ? error.message : 'Action failed')
        })
      }}
    >
      <span className="text-muted-foreground">{icon}</span>
      <span>{children}</span>
    </button>
  )
}
